import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReferralRecordService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the query/mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching the referral record list.
 *
 * @param {Object} params - Query parameters (page, limit, search, active_status, country,
 * registration_date_from, registration_date_to, sort_by, order_by)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useReferralRecordList = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["referral-record-list", params],
    queryFn: () => {
      return ReferralRecordService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching the distinct list of referrer countries, used to populate the
 * Country filter dropdown.
 *
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useReferralRecordCountries = (onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["referral-record-countries"],
    queryFn: () => {
      return ReferralRecordService.getCountries();
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 300000,
    cacheTime: 600000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching a single referral's full detail.
 *
 * @param {string} uuid - Referral UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useReferralRecord = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["referral-record", uuid],
    queryFn: () => {
      return ReferralRecordService.get(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Custom hook for fetching the reward-unlock history of the referral's referrer.
 *
 * The query is lazy-gated so the caller can defer fetching until, e.g., the
 * "Reward History" tab is actually selected.
 *
 * @param {string} uuid - Referral UUID
 * @param {Object} params - Query parameters (page, limit)
 * @param {Object} [options] - Extra options.
 * @param {boolean} [options.enabled=true] - Caller-controlled gate, ANDed with `!!uuid`.
 * @param {function} [options.onSuccess] - Callback to execute when the query is successful.
 * @param {function} [options.onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useReferralRecordRewardHistory = (uuid, params = {}, options = {}) => {
  const { enabled = true, onSuccess, onError = defaultError } = options;

  return useQuery({
    queryKey: ["referral-record-reward-history", uuid, params],
    queryFn: () => {
      return ReferralRecordService.getRewardHistory(uuid, params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid && enabled,
  });
};

/**
 * Custom hook for marking a referral as fraudulent with a required reason.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useMarkReferralFraudulent = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, reason }) => {
      return ReferralRecordService.markFraudulent(uuid, reason);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["referral-record-list"] });
      queryClient.invalidateQueries({ queryKey: ["referral-record", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export {
  useReferralRecordList,
  useReferralRecordCountries,
  useReferralRecord,
  useReferralRecordRewardHistory,
  useMarkReferralFraudulent,
};